/**
 * Input Sanitization Utility
 * ==========================
 * Provides comprehensive input sanitization to prevent XSS, injection attacks,
 * and data manipulation. All user inputs should be sanitized before storage.
 */

/**
 * Escape HTML entities to prevent XSS attacks
 * Only escapes characters that are structurally dangerous in HTML context:
 * - < becomes &lt;
 * - > becomes &gt;
 * - & becomes &amp;
 * - " becomes &quot;
 *
 * NOTE: Single quotes and forward slashes are NOT escaped because:
 * 1. They are normal text characters used in every language (I'm, he's, and/or)
 * 2. Escaping them corrupts stored data (e.g., "I'm" → "I&#x27;m")
 * 3. Modern frameworks handle attribute quoting safely
 * XSS via these characters is only possible in unquoted HTML attributes,
 * which is not a pattern used in React/modern frameworks.
 */
export const escapeHtml = (text: string): string => {
  if (typeof text !== 'string') {
    return '';
  }

  const map: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
  };

  return text.replace(/[&<>"]/g, char => map[char]);
};

/**
 * Sanitize string input by:
 * 1. Converting to string type
 * 2. Trimming whitespace
 * 3. Escaping HTML entities
 * 4. Enforcing max length
 */
export const sanitizeString = (
  value: any,
  maxLength?: number,
  allowHtml: boolean = false
): string => {
  // Handle non-string types
  if (typeof value === 'number' || typeof value === 'boolean') {
    value = String(value);
  }

  if (typeof value !== 'string') {
    return '';
  }

  // Trim whitespace
  value = value.trim();

  // Enforce max length
  if (maxLength && value.length > maxLength) {
    value = value.substring(0, maxLength);
  }

  // Escape HTML entities unless explicitly allowed
  if (!allowHtml) {
    value = escapeHtml(value);
  }

  return value;
};

/**
 * Sanitize email: lowercase and trim
 */
export const sanitizeEmail = (email: string): string => {
  const sanitized = sanitizeString(email, 255);
  return sanitized.toLowerCase().trim();
};

/**
 * Sanitize phone number: remove all non-numeric and non-formatting chars
 */
export const sanitizePhone = (phone: string): string => {
  const sanitized = sanitizeString(phone);
  // Keep only digits and common phone formatting
  return sanitized.replace(/[^\d\+\-\(\)\s]/g, '').trim();
};

/**
 * Sanitize integer: parse and enforce min/max bounds
 */
export const sanitizeInteger = (
  value: any,
  minVal?: number,
  maxVal?: number
): number | null => {
  try {
    const intVal = parseInt(String(value), 10);
    if (Number.isNaN(intVal)) {
      return null;
    }
    if (minVal !== undefined && intVal < minVal) {
      return minVal;
    }
    if (maxVal !== undefined && intVal > maxVal) {
      return maxVal;
    }
    return intVal;
  } catch {
    return null;
  }
};

/**
 * Sanitize boolean: convert to proper boolean value
 */
export const sanitizeBoolean = (value: any): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return ['true', '1', 'yes', 'on'].includes(value.toLowerCase());
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return false;
};

/**
 * Sanitize URL: validate and prevent open redirects
 */
export const sanitizeUrl = (url: string): string => {
  const sanitized = sanitizeString(url, 2048);

  try {
    const urlObj = new URL(sanitized);
    // Only allow http and https
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return '';
    }
    return sanitized;
  } catch {
    return '';
  }
};

/**
 * Sanitize filename: prevent path traversal and remove dangerous characters
 * Allows international characters (Unicode letters) in filenames.
 */
export const sanitizeFilename = (filename: string): string => {
  // Remove path components
  let sanitized = filename.split('/').pop()?.split('\\').pop() || '';

  // Remove dangerous characters but keep Unicode letters, digits, dots, dashes, underscores, spaces
  // \p{L} matches any Unicode letter, \p{N} matches any Unicode digit
  sanitized = sanitized.replace(/[^\p{L}\p{N}\s._-]/gu, '');

  // Remove leading dots and spaces (prevent hidden files / path traversal)
  sanitized = sanitized.replace(/^[\s.]+/, '');

  // Limit length
  if (sanitized.length > 255) {
    const lastDotIndex = sanitized.lastIndexOf('.');
    if (lastDotIndex > 0) {
      const name = sanitized.substring(0, lastDotIndex);
      const ext = sanitized.substring(lastDotIndex + 1);
      sanitized = name.substring(0, 250) + '.' + ext;
    } else {
      sanitized = sanitized.substring(0, 255);
    }
  }

  return sanitized || 'file';
};

/**
 * Sanitize JSON string: parse and re-serialize to remove injection attempts
 */
export const sanitizeJSON = (value: string): any => {
  if (typeof value !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    // Re-serialize to remove potential injection attempts
    return JSON.parse(JSON.stringify(parsed));
  } catch {
    return null;
  }
};

/**
 * Sanitize object data recursively
 */
export const sanitizeObject = (
  data: any,
  depth: number = 0,
  maxDepth: number = 10
): any => {
  if (depth > maxDepth) {
    console.warn(`Object sanitization exceeded max depth of ${maxDepth}`);
    return null;
  }

  if (data === null || data === undefined) {
    return data;
  }

  if (typeof data === 'string') {
    return sanitizeString(data);
  }

  if (typeof data === 'number' || typeof data === 'boolean') {
    return data;
  }

  if (data instanceof Date) {
    return data.toISOString();
  }

  if (Array.isArray(data)) {
    return data.map((item, idx) => {
      // Prevent deep nesting
      if (depth + 1 > maxDepth) return null;
      return sanitizeObject(item, depth + 1, maxDepth);
    });
  }

  if (typeof data === 'object') {
    const sanitized: any = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        // Sanitize key as well
        const sanitizedKey = sanitizeString(key, 255);
        if (depth + 1 <= maxDepth) {
          sanitized[sanitizedKey] = sanitizeObject(data[key], depth + 1, maxDepth);
        }
      }
    }
    return sanitized;
  }

  return String(data);
};

/**
 * Sanitize form data object
 * Recursively sanitizes all form values
 */
export const sanitizeFormData = (
  formData: Record<string, any>
): Record<string, any> => {
  if (!formData || typeof formData !== 'object') {
    return {};
  }

  const sanitized: Record<string, any> = {};

  for (const [key, value] of Object.entries(formData)) {
    // Skip empty values
    if (value === null || value === undefined) {
      continue;
    }

    // Handle different types
    if (Array.isArray(value)) {
      sanitized[key] = value.map((item) => {
        if (typeof item === 'string') {
          return sanitizeString(item);
        }
        if (typeof item === 'object') {
          return sanitizeObject(item);
        }
        return item;
      });
    } else if (typeof value === 'object' && !(value instanceof File)) {
      sanitized[key] = sanitizeObject(value);
    } else if (typeof value === 'string') {
      sanitized[key] = sanitizeString(value);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * Remove potentially dangerous content from text
 */
export const stripDangerousContent = (text: string): string => {
  if (typeof text !== 'string') {
    return '';
  }

  // Remove script tags and content
  let cleaned = text.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Remove event handlers
  cleaned = cleaned.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');
  cleaned = cleaned.replace(/on\w+\s*=\s*[^\s>]*/gi, '');

  // Remove iframe tags
  cleaned = cleaned.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');

  // Remove object and embed tags
  cleaned = cleaned.replace(/<(?:object|embed)\b[^<]*>/gi, '');

  return cleaned;
};

/**
 * Remove emojis from text
 * Strips all emoji and extended pictographic characters
 */
export const removeEmojis = (text: string): string => {
  if (typeof text !== 'string') {
    return '';
  }

  // Unicode emoji detection and removal
  const EMOJI_REGEX = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu;
  return text.replace(EMOJI_REGEX, '').trim();
};

/**
 * Remove non-text special symbols from text.
 * Preserves ALL international/Unicode letters (Nordic, Cyrillic, Arabic, CJK, etc.)
 * Only removes: emojis and control characters.
 */
export const removeSpecialSymbols = (text: string): string => {
  if (typeof text !== 'string') {
    return '';
  }

  // Remove only emojis and control characters — preserve all Unicode letters and punctuation
  const DISALLOWED_REGEX = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]|[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/gu;
  return text.replace(DISALLOWED_REGEX, '').trim();
};

/**
 * Sanitize text by removing emojis and control characters.
 * Preserves ALL international letters and normal punctuation.
 */
export const sanitizeTextForEmojisAndSymbols = (text: string): string => {
  if (typeof text !== 'string') {
    return '';
  }

  // First remove emojis, then remove other special symbols
  let cleaned = removeEmojis(text);
  cleaned = removeSpecialSymbols(cleaned);
  return cleaned.trim();
};

/**
 * Sanitize based on field type
 * Uses type-specific sanitization rules
 */
export const sanitizeByType = (
  value: any,
  fieldType: string,
  options?: any
): any => {
  if (value === null || value === undefined) {
    return value;
  }

  switch (fieldType) {
    case 'email':
      return sanitizeEmail(String(value));

    case 'phone':
      return sanitizePhone(String(value));

    case 'url':
      return sanitizeUrl(String(value));

    case 'integer':
      return sanitizeInteger(value, options?.min, options?.max);

    case 'boolean':
      return sanitizeBoolean(value);

    case 'title':
    case 'text':
      let sanitized = sanitizeString(String(value), options?.maxLength || 255, false);
      // Remove emojis and special symbols from titles and text fields by default
      if (!options?.allowEmojis) {
        sanitized = sanitizeTextForEmojisAndSymbols(sanitized);
      }
      return sanitized;

    case 'body':
      let bodySanitized = sanitizeString(String(value), options?.maxLength || 5000, options?.allowHtml || false);
      // Remove emojis and special symbols from body fields by default
      if (!options?.allowEmojis) {
        bodySanitized = sanitizeTextForEmojisAndSymbols(bodySanitized);
      }
      return bodySanitized;

    case 'file':
      // Files are handled separately, just return sanitized filename if it's a File object
      if (value instanceof File) {
        return {
          ...value,
          name: sanitizeFilename(value.name),
        };
      }
      return value;

    case 'dropdown':
      // For dropdowns, just trim and sanitize the string value
      return sanitizeString(String(value), 255, false);

    case 'date':
    case 'iso_datetime':
      // Keep dates as-is, they're already validated
      return value;

    default:
      return sanitizeString(String(value), 255, false);
  }
};
